import React, { useState } from 'react';
import { MessageCircle, Linkedin, Mail, Link as LinkIcon, Copy, ChevronRight, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { recordProfileShare } from '../../../api/PublicProfile/publicProfileApi';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody } from '../../../components/ui/Dialog';
import Button from '../../../components/ui/Button';

interface ProfileData {
  firstName?: string;
  lastName?: string;
  fullName: string;
  jobTitle: string;
  companyName: string;
  photo?: string | null;
  employeeId: string;
  slug?: string;
}

interface ShareMenuModalProps {
  profileData: ProfileData;
  profileUrl: string;
  onClose: () => void;
  onShare?: (platform: string) => void;
}

const ShareMenuModal: React.FC<ShareMenuModalProps> = ({ profileData, profileUrl, onClose, onShare }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const shareText = `Check out ${profileData?.fullName}'s professional profile - ${profileData?.jobTitle} at ${profileData?.companyName}`;

  // Track share event to backend API for analytics
  const trackShare = (platform: string) => {
    // Call the callback for client-side analytics
    onShare?.(platform);

    // Record share event to backend API (fire and forget)
    if (profileData?.slug) {
      recordProfileShare(profileData.slug).catch(console.error);
    }
  };

  const handleWhatsAppShare = () => {
    const url = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText + ' ' + profileUrl)}`;
    window.open(url, '_blank');
    trackShare('whatsapp');
  };

  const handleLinkedInShare = () => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(profileUrl)}`;
    window.open(url, '_blank');
    trackShare('linkedin');
  };

  const handleEmailShare = () => {
    const subject = encodeURIComponent(`${profileData?.fullName}'s Professional Profile`);
    const body = encodeURIComponent(`${shareText}\n\n${profileUrl}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    trackShare('email');
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard?.writeText(profileUrl);
      setCopied(true);
      trackShare('copy');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent size="sm">
        <DialogHeader className="p-6 border-b-0">
          <DialogTitle className="text-2xl font-bold text-foreground">{t('Share Profile')}</DialogTitle>
        </DialogHeader>

        <DialogBody className="px-6 pb-6 pt-0">
          {/* Profile Preview */}
          <div className="flex items-center gap-3 p-4 bg-muted rounded-lg mb-6">
            {profileData?.photo ? (
              <img
                src={profileData?.photo}
                alt={profileData?.fullName}
                className="w-12 h-12 rounded-full object-cover"
              />
            ) : (
              <div className="w-12 h-12 bg-gradient-to-br from-primary to-primary/70 rounded-full flex items-center justify-center">
                <span className="text-lg font-bold text-white">
                  {profileData?.firstName?.[0]}{profileData?.lastName?.[0]}
                </span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-foreground truncate">{profileData?.fullName}</p>
              <p className="text-sm text-muted-foreground truncate">{profileData?.jobTitle}</p>
            </div>
          </div>

          {/* Share Options */}
          <div className="space-y-3 mb-6">
            {/* WhatsApp - keeping brand green */}
            <button
              onClick={handleWhatsAppShare}
              className="w-full flex items-center gap-4 p-4 bg-card border-2 border-border rounded-xl hover:border-green-500 hover:bg-green-50 transition-all duration-200 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
            >
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center group-hover:bg-green-200 transition-colors">
                <MessageCircle className="w-6 h-6 text-green-600" />
              </div>
              <div className="flex-1 text-left">
                <p className="font-semibold text-foreground">{t('WhatsApp')}</p>
                <p className="text-sm text-muted-foreground">{t('Share via WhatsApp')}</p>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </button>

            {/* LinkedIn - keeping brand blue */}
            <button
              onClick={handleLinkedInShare}
              className="w-full flex items-center gap-4 p-4 bg-card border-2 border-border rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all duration-200 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                <Linkedin className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1 text-left">
                <p className="font-semibold text-foreground">{t('LinkedIn')}</p>
                <p className="text-sm text-muted-foreground">{t('Share on LinkedIn')}</p>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </button>

            {/* Email */}
            <button
              onClick={handleEmailShare}
              className="w-full flex items-center gap-4 p-4 bg-card border-2 border-border rounded-xl hover:border-primary hover:bg-primary/5 transition-all duration-200 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
            >
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                <Mail className="w-6 h-6 text-primary" />
              </div>
              <div className="flex-1 text-left">
                <p className="font-semibold text-foreground">{t('Email')}</p>
                <p className="text-sm text-muted-foreground">{t('Share via email')}</p>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </button>

            {/* Copy Link */}
            <button
              onClick={handleCopyLink}
              className="w-full flex items-center gap-4 p-4 bg-card border-2 border-border rounded-xl hover:border-primary hover:bg-primary/5 transition-all duration-200 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
            >
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                {copied ? (
                  <Check className="w-6 h-6 text-primary" />
                ) : (
                  <LinkIcon className="w-6 h-6 text-primary" />
                )}
              </div>
              <div className="flex-1 text-left">
                <p className="font-semibold text-foreground">{copied ? t('Copied!') : t('Copy Link')}</p>
                <p className="text-sm text-muted-foreground truncate">{profileUrl}</p>
              </div>
              {!copied && <Copy className="w-5 h-5 text-muted-foreground" />}
            </button>
          </div>

          {/* Cancel Button */}
          <Button
            variant="outline"
            fullWidth
            onClick={onClose}
          >
            {t('Cancel')}
          </Button>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};

export default ShareMenuModal;
